import DOMPurify from 'dompurify';
import moment from 'moment';
import { URLParams } from 'src/Models/URLParams';
import { UATConstants, WorkItemTypeIcons, WorkItemTypes } from '../Common/Models/UATConstants';
import { IDropdownOption } from 'office-ui-fabric-react';
import { MANAGE_ACTION_FIELDS } from 'src/manageAction/utils/manageActionConstants';

export const base64decodeURLParam = (param: string | null | undefined) => {
  return param == null || param.length === 0
    ? ''
    : b64DecodeUnicode(param.replace(/DECODEEQUAL/g, '='));
};

export const decodeURIParam = (param: string | null | undefined) => {
  return param == null || param.length === 0 ? '' : decodeURIComponent(param);
};

export const getURLParameter = (parm: string) => {
  const str = window.location.href;
  const rgx = new RegExp('\\b' + parm + '=.*\\b', 'gi');

  //this gets an array of matches
  const aMatches = str.match(rgx);
  if (aMatches == null) return;
  let parmVal = aMatches[0].substring(parm.length + 1);

  // make sure there are not trailing parms
  const idx = parmVal.indexOf('&');

  if (idx > -1) parmVal = parmVal.substring(0, idx);
  return parmVal;
};

export const getAllUrlParams = (url: string) => {
  // get query string from url (optional) or window
  let queryString = url ? url.split('?')[1] : window.location.search.slice(1);
  const obj = {};
  const paramObject: URLParams[] = [];
  if (queryString) {
    queryString = decodeURIParam(queryString).split('#')[0];
    const paramArray = queryString.split('&');
    for (let i = 0; i < paramArray.length; i++) {
      // separate the keys and the values
      const param = paramArray[i].split('=');
      const paramName = decodeURIParam(param[0]).trim().toLowerCase();
      let paramValue = [];
      paramValue.push(
        typeof param[1] === 'undefined' ? '' : decodeURIParam(param[1]).trim().toLowerCase()
      );
      if (typeof paramValue === 'string') paramValue = paramValue;
      if (!obj[paramName]) {
        obj[paramName] = paramValue;
        paramObject.push({
          fieldName: paramName,
          fieldValue: paramValue,
        });
      } else if (obj[paramName]) {
        obj[paramName] = [obj[paramName]];
        obj[paramName].push(paramValue);
        const objIndex = paramObject.findIndex((obj) => obj.fieldName == paramName);
        paramObject[objIndex].fieldValue.push(...paramValue);
      }
    }
  }
  return paramObject;
};

export const removeURLParameter = (param: string): void => {
  const url = new URL(window.location.href);
  url.searchParams.delete(param);
  window.history.replaceState({}, document.title, url.toString());
};

export const getIcon = (type: string) => {
  let output = [];
  switch (type) {
    case WorkItemTypes.epic: {
      output = ['epicType', WorkItemTypeIcons.epic];
      break;
    }
    case WorkItemTypes.feature: {
      output = ['featureType', WorkItemTypeIcons.feature];
      break;
    }
    case WorkItemTypes.pbi: {
      output = ['pbiType', WorkItemTypeIcons.pbi];
      break;
    }
    case WorkItemTypes.task: {
      output = ['taskType', WorkItemTypeIcons.task];
      break;
    }
    case WorkItemTypes.customerProject: {
      output = ['customerProjectType', WorkItemTypeIcons.customerProject];
      break;
    }
    case WorkItemTypes.feedback: {
      output = ['feedbackType', WorkItemTypeIcons.feedback];
      break;
    }
    case WorkItemTypes.action:
    case WorkItemTypes.actions: {
      output = ['actionsType', WorkItemTypeIcons.actions];
      break;
    }
    case WorkItemTypes.bug: {
      output = ['bugType', WorkItemTypeIcons.bug];
      break;
    }
    case WorkItemTypes.issue: {
      output = ['issueType', WorkItemTypeIcons.issue];
      break;
    }
    case WorkItemTypes.userStory: {
      output = ['userStory', WorkItemTypeIcons.userStory];
      break;
    }
    default: {
      output = ['defaultType', WorkItemTypeIcons.default];
      break;
    }
  }

  return output;
};

export const stateChartColor = (state: string) => {
  const ChartColors: any[] = [
    { key: 'In Progress', textColor: '#0a70b5', bgColor: '#E6F5FF' },
    { key: 'Done', textColor: '#088351', bgColor: '#EAFBF4' },
    { key: 'Escalated', textColor: '#EB3345', bgColor: '#FDE8EA' },
    { key: 'New', textColor: '#B2B2B2', bgColor: '#F2F2F2' },
    { key: 'Removed', textColor: '#D48ACF', bgColor: '#F8ECF7' },
    { key: 'On Hold', textColor: '#9b6208', bgColor: '#FFF5E6' },
    { key: 'Design', textColor: '#B87CE6', bgColor: '#F3EAFB' },
  ];

  return ChartColors.filter((c) => c.key === state);
};

export const trimTrailingChars = (s: any, charToTrim: any) => {
  const regExp = new RegExp(charToTrim + '+$');
  const result = s.replace(regExp, '');
  return result;
};

export const dateToYMD = (date: any) => {
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const y = date.getFullYear();
  return '' + y + '-' + (m <= 9 ? '0' + m : m) + '-' + (d <= 9 ? '0' + d : d);
};

export const b64DecodeUnicode = (str: any) => {
  try {
    // Going backwards: from bytestream, to percent-encoding, to original string.
    return decodeURIComponent(
      atob(str)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
  } catch (e) {
    return '';
  }
};

export const isValidURL = (str: string) => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i'
  ); // fragment locator
  return !!pattern.test(str);
};

export const sanitizeText = (text: string, sendFlag: boolean = false) => {
  let flag = false;
  const config = {
    KEEP_CONTENT: true,
  };

  const sanitizedText = DOMPurify.sanitize(text, config);
  if (DOMPurify.removed.length > 0) {
    flag = true;
  }

  if (sendFlag) {
    return [flag, sanitizedText];
  } else {
    return sanitizedText;
  }
};

export const accomodateUnresolvedUser = (displayName: string) => {
  if (displayName === undefined || displayName === null) return '';
  return displayName.toLowerCase().endsWith(UATConstants.atMicrosoft)
    ? displayName.toLowerCase()
    : '';
};

export const getRichTextValue = (content: string) => {
  if (/<img\s[^>]*>/i.test(content)) {
    return content;
  }
  return stripHtml(content).trim() === '' ? '' : content;
};

export function removeSpacesFromHtmlString(html: string) {
  return html
    .replace(/\s+/g, '') // Replace spaces with nothing
    .replace(/>\s+</g, '><') // Remove spaces between tags
    .replace(/<!--.*?-->/g, ''); // Remove comments
}

export const IsRteContentChangedOnBlur = (previuos: string, current: string): boolean => {
  return removeSpacesFromHtmlString(previuos) !== removeSpacesFromHtmlString(current);
};

export const getRichURL = (content: string) => {
  if (content === undefined) return '';
  content = stripHtml(content).trim() === '' ? '' : content;
  if (content.includes('&amp')) {
    return content.replace(/&amp;/g, '&');
  }
  return content;
};

export const removeCurrencySign = (newText: string) => {
  if (newText === undefined) return '';
  const CurrencyVal = newText !== '' ? newText.replace(/\$|,/g, '') : '';
  return CurrencyVal;
};

export const convertDecimal = (newText: string): number => {
  if (newText !== undefined && newText !== null && newText !== '') {
    const decimalRegex = /^(\d*\.)?\d+$/;

    const isValid = newText !== '' ? decimalRegex.test(newText) : true;
    const decimalVal = isValid ? parseFloat(newText) : 0;

    return decimalVal;
  }
  return 0;
};
// converts  464329.88 to 464,329.88 or 2428632 to 2,428,632.00 or 77910.8 to 77,910.80
export const convertToCurrencyFormat = (
  newText: string,
  decimalRequired: boolean = true,
  isNegative: boolean = false
) => {
  if (newText !== undefined && newText !== null && newText !== '') {
    const n = decimalRequired ? Number.parseFloat(newText) : Number.parseInt(newText);
    if (!newText || isNaN(n) || (n < 0 && !isNegative)) return '0.00';
    let formattednewText = decimalRequired ? n.toFixed(2) : n;
    formattednewText = formattednewText.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return formattednewText;
  }
  return '0';
};

// converts  464329.88 to 464,329.88 or 2428632 to 2,428,632
export const convertToCurrencyFormatWithUptoFixedPlaces = (newText: string) => {
  if (newText !== undefined && newText !== null && newText !== '') {
    if (newText === '0') return newText;
    // check takes care of ',' for each type in
    const n =
      newText.toString().indexOf(',') !== -1
        ? Number.parseFloat(newText.replace(',', ''))
        : Number.parseFloat(newText);
    if (isNaN(n)) return '0';
    const defaultUptoDecimalPlaces = UATConstants.defaultUptoDecimalPlaces;
    const uptoDecimalPlaces =
      newText.toString().indexOf('.') >= 0
        ? newText.toString().substr(newText.toString().indexOf('.'), newText.length).length - 1
        : 0;
    const formattednewText = n
      .toFixed(
        uptoDecimalPlaces > defaultUptoDecimalPlaces ? defaultUptoDecimalPlaces : uptoDecimalPlaces
      )
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return formattednewText;
  }
  return '';
};

export const convertToCurrencyFormatWithUptoFixedPlacesAndRoundOff = (value: string): string => {
  if (value !== undefined && value !== null && value !== '') {
    const numericValue: number = Number.parseFloat(value.replace(/[^0-9.]/g, ''));
    if (isNaN(numericValue) || numericValue < 0) {
      return '';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
  }
  return '';
};

export const currencyWithoutSymbolToNumberDigitsFormat = (currencyValue: string) => {
  //Sample Input: 1,234,567.89 & Sample Output: 1234567.89
  return currencyValue.replace(/,/g, '');
};

export const checkInputInCorrectFormat = (value: string): string => {
  if (value !== undefined && value !== null && value !== '') {
    let cleanedValue = value.replace(/[^0-9.]/g, '');
    const decimalIndex = cleanedValue.indexOf('.');
    if (decimalIndex !== -1) {
      cleanedValue =
        cleanedValue.slice(0, decimalIndex + 1) +
        cleanedValue.slice(decimalIndex + 1).replace(/\./g, '');
    }

    // Return the cleaned and validated value
    // Ensure the string is a valid number or empty if it's not valid
    return /^\d*\.?\d*$/.test(cleanedValue) ? cleanedValue : '';
  }
  return '';
};

export const checkInputIsAlphanumeric = (value: string): string => {
  if (value !== undefined && value !== null && value !== '') {
    // Remove all characters that are not alphanumeric
    const cleanedValue = value.replace(/[^a-zA-Z0-9]/g, '');

    // Check if the cleaned value matches the alphanumeric pattern
    return /^[a-zA-Z0-9]+$/.test(cleanedValue) ? cleanedValue : '';
  }
  return '';
};

export const checkInputIsAlphanumericWithHyphen = (value: string): string => {
  if (value !== undefined && value !== null && value !== '') {
    // Remove all characters that are not alphanumeric or hyphen
    const cleanedValue = value.replace(/[^a-zA-Z0-9-]/g, '');

    // Check if the cleaned value matches the alphanumeric with hyphen pattern
    return /^[a-zA-Z0-9-]+$/.test(cleanedValue) ? cleanedValue : '';
  }
  return '';
};

export const checkInputIsAlphanumericWithSpecialChars = (value: string): string => {
  if (value !== undefined && value !== null && value !== '') {
    // Remove all characters that are not alphanumeric, hyphen, comma, space, or semicolon
    const cleanedValue = value.replace(/[^a-zA-Z0-9-,; ]/g, '');

    // Check if the cleaned value matches the alphanumeric with hyphen, comma, space, or semicolon pattern
    return /^[a-zA-Z0-9-,; ]+$/.test(cleanedValue) ? cleanedValue : '';
  }
  return '';
};

export const formatToDollar = (value: string): string => {
  const number = parseFloat(value);
  if (isNaN(number)) {
    return '0';
  }
  const isNegative = number < 0;
  const formattedNumber = number.toLocaleString('en-US', {
    minimumFractionDigits: 2, // Ensure two decimal places
    maximumFractionDigits: 2,
  });
  const formattedValue = isNegative ? `${formattedNumber}` : `${formattedNumber}`;
  return formattedValue;
};

const LOREM_IPSUM = (
  'lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut ' +
  'labore et dolore magna aliqua ut enim ad minim veniam quis nostrud exercitation ullamco laboris nisi ut ' +
  'aliquip ex ea commodo consequat duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore ' +
  'eu fugiat nulla pariatur excepteur sint occaecat cupidatat non proident sunt in culpa qui officia deserunt '
).split(' ');

let loremIndex = 0;
export function _lorem(wordCount: number): string {
  const startIndex = loremIndex + wordCount > LOREM_IPSUM.length ? 0 : loremIndex;
  loremIndex = startIndex + wordCount;
  return LOREM_IPSUM.slice(startIndex, loremIndex).join(' ');
}

export function _randomFileSize(): { value: string; rawSize: number } {
  const fileSize: number = Math.floor(Math.random() * 100) + 30;
  return {
    value: `${fileSize} KB`,
    rawSize: fileSize,
  };
}

export function _randomDate(start: Date, end: Date): { value: number; dateFormatted: string } {
  const date: Date = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
  return {
    value: date.valueOf(),
    dateFormatted: date.toLocaleDateString(),
  };
}

export const formatDate = (content: string): string => {
  try {
    if (moment(content).isValid()) {
      return moment(content).format(UATConstants.DefaultDateFormat);
    } else {
      return '';
    }
  } catch (err) {
    return '';
  }
};
export const formatExcelDate = (content: string): string => {
  try {
    if (moment(content).isValid()) {
      return moment(content).format(UATConstants.ExcelDateFormat);
    } else {
      return '';
    }
  } catch (err) {
    return '';
  }
};

export const convertUTCDateToLocal = (utcDateString: string | null | undefined): string => {
  if (!utcDateString) {
    return 'Invalid date';
  }

  const utcDate = new Date(utcDateString);
  if (isNaN(utcDate.getTime())) {
    return 'Invalid date';
  }

  const year = utcDate.getUTCFullYear();
  const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(utcDate.getUTCDate()).padStart(2, '0');

  let hours = utcDate.getUTCHours();
  const minutes = String(utcDate.getUTCMinutes()).padStart(2, '0');

  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return `${month}/${day}/${year} ${String(hours).padStart(2, '0')}:${minutes} ${period}`;
};

export const convertUTCDateToDateOnly = (utcDateString: string | null | undefined): string => {
  if (!utcDateString) {
    return '';
  }

  const utcDate = new Date(utcDateString);
  if (isNaN(utcDate.getTime())) {
    return '';
  }

  const year = utcDate.getUTCFullYear();
  const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(utcDate.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const formatCurrency = (content: string): string => {
  try {
    if (moment(content).isValid()) {
      return moment(content).format(UATConstants.DefaultDateFormat);
    } else {
      return '';
    }
  } catch (err) {
    return '';
  }
};

export const formatContent = (html: any) => {
  try {
    if (!isNaN(html)) {
      return html;
    } else {
      return stripHtml(html);
    }
  } catch (err) {
    return '';
  }
};

export const stripHtml = (html: string) => {
  // Create a new div element
  const temporalDivElement = document.createElement('div');
  // Set the HTML content with the providen
  temporalDivElement.innerHTML = html;
  // Retrieve the text property of the element (cross-browser support)
  return temporalDivElement.textContent || temporalDivElement.innerText || '';
};

export const stripAngleBracketContent = (value?: string): string =>
  (value || '').replace(/<[^>]*>/g, '').trim();

export const getAvatarInitials = (value?: string): string => {
  const cleanedValue = stripAngleBracketContent(value);
  if (!cleanedValue) {
    return '';
  }

  const parts = cleanedValue.split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return '';
  }

  const firstInitial = parts[0].charAt(0).toUpperCase();
  if (parts.length === 1) {
    return firstInitial;
  }

  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
  return `${firstInitial}${lastInitial}`;
};

export const removeHTMLFromArrayObject = (data: any) => {
  return data.map((item: any) => {
    const newItem: { [key: string]: any } = {};
    for (const key in item) {
      if (typeof item[key] === 'string' && /<\/?[^>]+(>|$)/g.test(item[key])) {
        newItem[key] = stripHtml(item[key]);
      } else {
        newItem[key] = item[key];
      }
    }
    return newItem;
  });
};

export const extractNum = (numStr: string) => {
  let num: number | string | false | null = -1;
  if (numStr == null) {
    return num;
  }

  const result = numStr.match(/\d+/);
  num = result && result.length > 0 && result[0];
  return num;
};

export const isNumeric = (num: string) => !isNaN(Number(num));
export const getIconType = (type: string) => {
  switch (type) {
    case 'Product Backlog Item':
      return {
        icontype: 'PageListMirroredSolid',
        iconClass: 'pbiType',
        title: type,
      };
    case 'Feature':
      return {
        icontype: 'Trophy2Solid',
        iconClass: 'featureType',
        title: type,
      };
    case 'Feature Request':
      return {
        icontype: 'AsteriskSolid',
        iconClass: 'defaultType',
        title: type,
      };
    case 'Epic':
      return {
        icontype: 'CrownSolid',
        iconClass: 'epicType',
        title: type,
      };
    case 'Customer Project':
      return {
        icontype: 'FinancialSolid',
        iconClass: 'customerProjectType',
        title: type,
      };
    case 'Action':
    case 'Actions':
      return {
        icontype: 'TaskSolid',
        iconClass: 'actionsType',
        title: type,
      };
    case 'Task':
      return {
        icontype: 'TaskSolid',
        iconClass: 'taskType',
        title: type,
      };
    case 'Feedback':
      return {
        icontype: 'FlameSolid',
        iconClass: 'feedbackType',
        title: type,
      };
    case 'Issue':
      return {
        icontype: 'ConstructionConeSolid',
        iconClass: 'issueType',
        title: type,
      };
    case 'Bug':
      return {
        icontype: 'LadybugSolid',
        iconClass: 'bugType',
        title: type,
      };
    default:
      return {
        icontype: 'defaultType',
        iconClass: 'AsteriskSolid',
        title: '',
      };
  }
};

export const SortDropdownOptionsAlphabeticalOrder = (list: IDropdownOption[]) => {
  list.sort((a, b) => {
    if (a.key === 0) {
      return -1;
    }
    if (b.key === 0) {
      return 1;
    }
    const firstText = a.text.toUpperCase(); // ignore upper and lowercase
    const secondText = b.text.toUpperCase(); // ignore upper and lowercase
    if (firstText < secondText) {
      return -1;
    }
    if (firstText > secondText) {
      return 1;
    }
    return 0;
  });
  return list;
};

export const SortDropdownOptionsByValue = (list: IDropdownOption[], placeHolderText: string) => {
  const selectOptionItem = list.find((item) => item.text === placeHolderText);
  list = list.filter((item) => item !== selectOptionItem);
  list.sort((a, b) => (a.text > b.text ? 1 : b.text > a.text ? -1 : 0));
  list.unshift(selectOptionItem as any);
  return list;
};
export const purifyText = (text: string) => {
  const data = sanitizeText(text, true);
  if (data && data[0]) {
    return data[1].toString();
  }
  return text;
};

export const hasValue = (value: string | null | undefined): boolean => {
  if (value && value !== null && value !== undefined && value.trim().length > 0) {
    return true;
  }
  return false;
};

export const getAssigneeForSingleUser = (
  value: string | null | undefined,
  uniqueName: string | null | undefined
): any[] => {
  if (hasValue(value)) {
    return [
      {
        key: value,
        text: value,
        title: uniqueName,
      },
    ];
  } else {
    return [];
  }
};
// TODO: This function should be modified / removed in favour of ManageActionRedirection.tsx
export const redirectionGuide = (nodeName: string) => {
  if (window.location.pathname === UATConstants.Routes.ManageActionPage) {
    if (
      nodeName === MANAGE_ACTION_FIELDS.CoSell ||
      nodeName === MANAGE_ACTION_FIELDS.GPS ||
      nodeName === MANAGE_ACTION_FIELDS.RedButtonTSI ||
      nodeName === MANAGE_ACTION_FIELDS.SMCRedButton ||
      nodeName === MANAGE_ACTION_FIELDS.CAPSL ||
      nodeName === MANAGE_ACTION_FIELDS.GSI ||
      nodeName === MANAGE_ACTION_FIELDS.PMYT ||
      nodeName === MANAGE_ACTION_FIELDS.McapsMacc
    ) {
      const redirectionUrl = window.location.href.replace(
        UATConstants.Routes.ManageActionPage,
        UATConstants.Routes.ManageAction
      );
      window.location.replace(redirectionUrl);
    }
  }
  if (window.location.pathname === UATConstants.Routes.ManageAction) {
    if (
      !(
        nodeName === MANAGE_ACTION_FIELDS.CoSell ||
        nodeName === MANAGE_ACTION_FIELDS.GPS ||
        nodeName === MANAGE_ACTION_FIELDS.RedButtonTSI ||
        nodeName === MANAGE_ACTION_FIELDS.SMCRedButton ||
        nodeName === MANAGE_ACTION_FIELDS.CAPSL ||
        nodeName === MANAGE_ACTION_FIELDS.PMYT ||
        nodeName === MANAGE_ACTION_FIELDS.GSI ||
        nodeName === MANAGE_ACTION_FIELDS.McapsMacc
      )
    ) {
      const redirectionUrl = window.location.href.replace(
        UATConstants.Routes.ManageAction,
        UATConstants.Routes.ManageActionPage
      );
      window.location.replace(redirectionUrl);
    }
  }
};
export const convertToCurrencyRevenueFormat = (value: string): string => {
  if (value !== undefined && value !== null && value !== '') {
    const numericValue: number = Number.parseFloat(value.replace(/[^0-9.]/g, ''));
    if (isNaN(numericValue) || numericValue < 0) {
      return '$0.00';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
  }
  return '';
};

export const access = {
  ADMIN: 'Admin',
  EDIT: 'Edit',
  VIEW: 'View',
};
export const all = 'manageAction';

const Admin = ['Actions/GPS/Admin', 'Actions/RoB/Owner-Accexec-Req'];
const Edit = ['Actions/GPS/Edit', 'Actions/RoB/Views/All', 'Actions/GPS/BPO'];
const View = ['Actions/GPS/View'];

export const overAllAccess = [Admin, Edit, View];

export const manageAction = {
  addEditView: 'addEditView',
  headerSection: 'headerSection',
  rightHandSection: 'rightHandSection',
  leftHandSection: 'leftHandSection',
};

export const leftHandSection = {
  actionDetailTabs: 'actionDetailTabs',
  basicInformation: 'basicInformation',
  requesterInformation: 'requesterInformation',
  opportunityInformation: 'opportunityInformation',
  partnerInformation: 'partnerInformation',
  dealInformation: 'dealInformation',
  leftHandSection: 'leftHandSection',
};

export const rightHandSection = {
  coPilot: 'coPilot',
  notificationBanners: 'notificationBanners',
  statusUpdate: 'statusUpdate',
  closureStatement: 'closureStatement',
  discussion: 'discussion',
  emailCommunication: 'emailCommunication',
  rightHandSection: 'rightHandSection',
};

export const headerSection = {
  actionHeader: 'actionHeader',
  actionTitle: 'actionTitle',
  actionLinks: 'actionLinks',
  actionStatus: 'actionStatus',
  actionTagPicker: 'actionTagPicker',
  notificationBanners: 'notificationBanners',
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { notificationBanners, ...otherProperties } = headerSection;
export const headerWithoutBanner = { ...otherProperties };

export const NoGPSAccess = [
  'NoAccess',
  ...Object.values(headerWithoutBanner),
  ...Object.values(leftHandSection),
];
type Item = {
  [key: string]: any;
};

export const getKeysFromArray = (items: Item[]): string[] => {
  return items.map((item) => item.key);
};

export const convertToString = (items: Item[], key?: string = 'text'): string => {
  return items[0][key];
};

export const SortDropdownOptionsAlphabetical = (
  list: IDropdownOption[],
  excludeOptions: string
) => {
  list?.sort((a, b) => {
    if (a.key === excludeOptions) {
      return -1;
    }
    if (b.key === excludeOptions) {
      return 1;
    }

    // Sort the rest alphabetically by the `text` property
    const firstText = a.text.toUpperCase(); // ignore case
    const secondText = b.text.toUpperCase(); // ignore case
    if (firstText < secondText) {
      return -1;
    }
    if (firstText > secondText) {
      return 1;
    }
    return 0;
  });

  return list;
};

export const parseCurrencyValue = (currencyValue: string) => {
  const currency = parseFloat(
    typeof currencyValue === 'string' ? currencyValue.replace(/,/g, '') : currencyValue
  );
  return isNaN(currency) ? 0 : currency;
};

export const currencyPayloadTypeChange = (input: any) => {
  if (input === '' || input === 0 || input === null || input === undefined) {
    return 0;
  }
  if (typeof input === 'string') {
    return parseFloat(input.replace(/,/g, '')) || 0;
  }
  return parseFloat(input) || 0;
};

export const parseDateField = (dateField: string) => {
  const date = dateField ? new Date(dateField).toDateString() : undefined;
  return date;
};

export const isArtemisTenantFn = (tenantId: string, tenantConfigs: any[]) => {
  if (!tenantId) return false; // Early return if tenantId is empty or undefined
  if (!Array.isArray(tenantConfigs)) return false; // Early return if tenantConfigs is undefined, null, or not an array

  return tenantConfigs.some(
    (tenantConfig) => tenantConfig.TenantID === tenantId && tenantConfig.ArtemisCall === true
  );
};

export const disableSplitMode = (tenantConfigs: string[]) => {
  const disableSplitModeTenantName: string[] = [
    // UATConstants.PlaceholdersAndTitles.ECIFPreApproval_Category, //disabled split mode for ECIF Pre Approval
  ];
  if (!Array.isArray(tenantConfigs)) return false; // Early return if tenantConfigs is undefined, null, or not an array

  // Check if any tenantConfig name is in the disableSplitModeTenantName array
  return tenantConfigs.some((tenantConfig) => disableSplitModeTenantName.includes(tenantConfig));
};

export const removeCurrencyFormat = (value: any): number => {
  if (typeof value === 'string') {
    // Remove any non-numeric characters except for the decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    return parseFloat(numericValue) || 0;
  }
  return value;
};

export const convertToEmailString = (
  items: Array<{ key: string; title: string; text: string }>
): string => {
  if (!items || !items.length) return '';
  // Map each item to its key (which contains the email) and join with semicolons
  return items.map((item) => item.key).join(';');
};

//AMM Start
export const removeTimeFromDate = (date?: Date | null) => {
  if (date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  } else {
    return null;
  }
};

export const extractTimeFromDate = (AMM_CaseCreatedTime: string) => {
  const dateObject = new Date(AMM_CaseCreatedTime);
  let hours = dateObject.getHours();
  const minutes = dateObject.getMinutes();
  const period = hours < 12 ? 'AM' : 'PM';
  hours = +hours % 12 || 12;
  const formattedTime = `${hours}:${minutes < 10 ? '0' : ''}${minutes} ${period}`;
  return formattedTime;
};

// transform the values to the format required by the dropdown
export const getPeopleFromStringValue = (emailList: string) => {
  if (emailList && emailList != '') {
    return emailList.split(',').map(() => {
      return { key: emailList, title: emailList, text: emailList };
    });
  }
  return [];
};

export const getPeopleValue = (displayName: string, uniqueName: string) => {
  if (uniqueName && uniqueName != '') {
    return [{ key: uniqueName, title: uniqueName, text: displayName }];
  } else if (displayName && displayName != '') {
    return [{ key: uniqueName, title: uniqueName, text: displayName }];
  }
  return [];
};
// Generated by GitHub Copilot | Updated on: 2026-05-13 | 1 lines modified | Added Actions/GPS/BPO to Edit access array to enable bulk upload and download template for BPO role

export const incrementRevenueROIConvertion = (incrementalRevenueROI: string) => {
  if (!incrementalRevenueROI) {
    //  return [];
    return { transformedValue: [], FinancialYear: [] };
  }
  const purify = incrementalRevenueROI.replace(/(<([^>]+)>)/gi, '');
  const decodedValue = purify?.replace(/&quot;/g, '"');
  //  Parse the JSON string into an array of objects
  const parsedValue =
    decodedValue !== 'null' && decodedValue !== null && decodedValue !== ''
      ? JSON.parse(decodedValue)
      : [];
  // Transform the parsed data into the desired format
  const transformedValue = parsedValue.map((item: { key: string; text: string }) => ({
    financialYear: item.key,
    revenue: item.text,
  }));
  const FinancialYear: string[] = (parsedValue && parsedValue.map((item) => item.key)) || [];

  return { transformedValue, FinancialYear };
};

export const transformFinancialYearRevenue = (
  incrementalRevenueRoi: Array<{ key: string; text: string }>
) => {
  return incrementalRevenueRoi.map((item) => ({
    financialYear: item.key,
    revenue: convertToCurrencyRevenueFormat(item.text),
  }));
};

export const formatDateInMMDDYYYY = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

//AMM End

/**
 * Check if COMPETE special fields should be shown based on tenant, action owner, assigned team and tags
 * @param actionOwner - The unique name/email of the action owner
 * @param assignedTeam - The area path of the assigned team
 * @param tags - The tags string from the work item (System.Tags)
 * @returns boolean indicating if special fields should be shown
 */
export const shouldShowCompeteSpecialFields = (assignedTeam: string, tags: string): boolean => {
  const hasCompeteTag = tags?.includes('Copilot Compete') || false;
  const isCompeteTeam =
    assignedTeam.includes(UATConstants.TechRoBAreaPaths.WCBABSTriage) ||
    assignedTeam.includes(UATConstants.TechRoBAreaPaths.WCBCustomerSuccessABSScrum) ||
    assignedTeam.includes(
      UATConstants.TechRoBAreaPaths.WCBMicrosoftSolutionsABSScrum
    );
  return hasCompeteTag && isCompeteTeam ? true : false;
};

/**
 * Flags used to determine whether the Email section should be hidden.
 * These map to the corresponding validation flags on the Details tab component state.
 */
export interface EmailSectionVisibilityFlags {
  qbcValidationsNeeded: boolean;
  NoiminationTabValidationNeeded: boolean;
  AHPMValidationNeeded: boolean;
  gsmoValidationsNeeded: boolean;
  isMSAmericasSEandO: boolean;
  isAmericasOcto: boolean;
  RedButtonValidationNeeded: boolean;
  CoSellValidationNeeded: boolean;
  CAPSLValidationNeeded: boolean;
  AMMValidationNeeded: boolean;
  AOAIValidationNeeded: boolean;
  ECIFValidationNeeded: boolean;
  MAASValidationNeeded: boolean;
  MACCValidationNeeded: boolean;
  GPSValidationNeeded: boolean;
  isApexType: boolean;
  TechRoBValidationNeeded: boolean;
  selectedPath: string;
  isCompete: boolean;
}

/**
 * Determine whether the Email section should be hidden.
 * The Email section is hidden for Tech ROB, Compete, MS Fed and General actions.
 * A "General" action is one that does not belong to any special tenant/validation type,
 * so the Email section is only shown when one of those special tenants applies.
 * @param flags - The current validation flags / area path / compete indicator
 * @returns boolean indicating if the Email section should be hidden
 */
export const shouldHideEmailSection = (flags: EmailSectionVisibilityFlags): boolean => {
  const isSpecialTenant =
    flags.qbcValidationsNeeded ||
    flags.NoiminationTabValidationNeeded ||
    flags.AHPMValidationNeeded ||
    flags.gsmoValidationsNeeded ||
    flags.isMSAmericasSEandO ||
    flags.isAmericasOcto ||
    flags.RedButtonValidationNeeded ||
    flags.CoSellValidationNeeded ||
    flags.CAPSLValidationNeeded ||
    flags.AMMValidationNeeded ||
    flags.AOAIValidationNeeded ||
    flags.ECIFValidationNeeded ||
    flags.MAASValidationNeeded ||
    flags.MACCValidationNeeded ||
    flags.GPSValidationNeeded ||
    flags.isApexType;

  const isTechRoB = flags.TechRoBValidationNeeded;
  const isMSFederal = flags.selectedPath === UATConstants.MicrosoftFederalRootAreaPath;

  // Hide for Tech ROB, Compete, MS Fed and General (no special tenant) actions.
  return isTechRoB || flags.isCompete || isMSFederal || !isSpecialTenant;
};

/**
 * Utility function to check if RTE value is effectively empty
 * Handles common empty states that Rich Text Editors produce
 * @param value - The string value to check
 * @returns true if the value is effectively empty, false otherwise
 */
export const isRTEValueEmpty = (value: string): boolean => {
  return (
    !value ||
    value.trim() === '' ||
    value === '<div><br></div>' ||
    value === '<p><br></p>' ||
    value === '<br>' ||
    value?.replace(/<[^>]*>/g, '').trim() === ''
  );
};
// Generated by GitHub Copilot | Updated on: 2026-07-13 | 18 lines modified | Added convertUTCDateToDateOnly for YYYY-MM-DD export formatting
